import {
  BadgeCheck,
  Bell,
  CalendarClock,
  DoorOpen,
  Megaphone,
  MessageSquare,
  Rocket,
  Split,
  type LucideIcon,
} from 'lucide-react';

import type { NotificationItemDto } from '@/api/notifications-client';
import type { StatusTone } from '@/components/ui/status-badge';

// =============================================================================
// notification-presentation.ts — Mapping PRÉSENTATION du centre de notifications (R15-COM-06).
// =============================================================================
// Fonctions PURES (testables sans DOM) : catégorie → { libellé FR, icône lucide }, colorCode → tone du
// Design System (G-5 : jamais de couleur hex en dur ; le couple couleur+libellé double l'information —
// WCAG 1.4.1), et RÉSOLUTION DE NAVIGATION depuis `payloadContext` (ids uniquement, jamais de contenu).
//
// ANTI-LLMISM (invariant n°6) : aucun em-dash en substitut parenthétique dans les libellés produits.

/** Métadonnées de présentation d'une catégorie (libellé FR + icône de type). */
export interface CategoryMeta {
  readonly label: string;
  readonly Icon: LucideIcon;
}

/**
 * Catégorie → { libellé FR, icône }. Table exhaustive des catégories connues (énum backend). Une valeur
 * inconnue (évolution serveur) retombe sur un défaut neutre via `categoryMeta()`.
 */
const CATEGORY_META: Readonly<Record<string, CategoryMeta>> = {
  LancementCampagne: { label: 'Lancement de campagne', Icon: Rocket },
  RappelJ3: { label: 'Rappel J-3', Icon: CalendarClock },
  RappelJ1: { label: 'Rappel J-1', Icon: CalendarClock },
  Validation: { label: 'Validation', Icon: BadgeCheck },
  Phasage: { label: 'Changement de phase', Icon: Split },
  ReouvertureNego: { label: 'Réouverture de négociation', Icon: DoorOpen },
  Annonce: { label: 'Annonce JeKo', Icon: Megaphone },
  NouveauMsgChat: { label: 'Nouveau message', Icon: MessageSquare },
};

/** Défaut neutre pour une catégorie inconnue (résilience aux valeurs futures). */
const DEFAULT_CATEGORY_META: CategoryMeta = { label: 'Notification', Icon: Bell };

/** Résout la présentation d'une catégorie (libellé FR + icône). Jamais d'exception sur valeur inconnue. */
export function categoryMeta(category: string): CategoryMeta {
  return CATEGORY_META[category] ?? DEFAULT_CATEGORY_META;
}

/**
 * colorCode (énum backend {Bleu,Orange,Rouge,Vert,Gris}) → tone du Design System (StatusTone). Le tone
 * pilote un TOKEN sémantique (badge status AA-safe) — jamais une couleur hex en dur (G-5). Le LIBELLÉ de
 * catégorie double toujours la couleur (WCAG 1.4.1 : la couleur n'est jamais le seul porteur d'info).
 * Valeur inconnue → tone neutre `inactive`.
 */
export function colorCodeToTone(colorCode: string): StatusTone {
  switch (colorCode) {
    case 'Bleu':
      return 'invited';
    case 'Orange':
      return 'suspended';
    case 'Rouge':
      return 'refused';
    case 'Vert':
      return 'active';
    case 'Gris':
      return 'inactive';
    default:
      return 'inactive';
  }
}

/** Cible de navigation TanStack Router résolue depuis `payloadContext`. `null` = aucune cible (mark-read seul). */
export interface NotificationNavTarget {
  readonly to: string;
  readonly params?: Record<string, string>;
  readonly search?: Record<string, unknown>;
}

/** Lit un champ string non-vide de `payloadContext` (ids), sinon `undefined`. */
function readId(
  payload: Record<string, unknown>,
  key: string,
): string | undefined {
  const value = payload[key];
  return typeof value === 'string' && value.length > 0 ? value : undefined;
}

/** Lit le cycle (numérique) de `payloadContext` s'il est présent et entier > 0. */
function readCycle(payload: Record<string, unknown>): number | undefined {
  const value = payload['cycle'];
  if (typeof value === 'number' && Number.isInteger(value) && value > 0) {
    return value;
  }
  if (typeof value === 'string' && /^\d+$/.test(value)) {
    const parsed = Number(value);
    return parsed > 0 ? parsed : undefined;
  }
  return undefined;
}

/**
 * Résout la NAVIGATION au clic depuis (catégorie, payloadContext). Ne consomme QUE des identifiants
 * (jamais de contenu). Cibles vers des ROUTES EXISTANTES (router.tsx) uniquement :
 *   • Catégories campagne (Lancement/Rappels/Validation/Phasage/RéouvertureNégo) :
 *       - submissionId présent → /saisie/$submissionId (+ ?cycle si cycle ≥ 2, parité dashboard) ;
 *       - sinon campaignId présent → /campagnes?campaign=<id> (détail campagne) ;
 *   • Annonce / NouveauMsgChat : aucune route dédiée en V1 (annonces §3.9 onglet 3 = V1.5 ; messagerie
 *     hors périmètre de ce lot) → `null` : le clic marque lu SANS naviguer (pas de 404 fabriqué). Différé
 *     documenté (à câbler à la livraison des routes annonces/messagerie).
 */
export function resolveNotificationTarget(
  item: Pick<NotificationItemDto, 'category' | 'payloadContext'>,
): NotificationNavTarget | null {
  const { category, payloadContext } = item;
  switch (category) {
    case 'LancementCampagne':
    case 'RappelJ3':
    case 'RappelJ1':
    case 'Validation':
    case 'Phasage':
    case 'ReouvertureNego': {
      const submissionId = readId(payloadContext, 'submissionId');
      if (submissionId !== undefined) {
        const cycle = readCycle(payloadContext);
        return {
          to: '/saisie/$submissionId',
          params: { submissionId },
          search: cycle !== undefined && cycle >= 2 ? { cycle } : {},
        };
      }
      const campaignId = readId(payloadContext, 'campaignId');
      if (campaignId !== undefined) {
        // GATE_HUMAN 2026-09-11 : /conditions retirée ; /campagnes accepte le même état d'URL `?campaign`.
        return { to: '/campagnes', search: { campaign: campaignId } };
      }
      return null;
    }
    // Annonce / NouveauMsgChat : pas de route V1 (cf. entête). Différé.
    default:
      return null;
  }
}

/** Vrai si la notification est NON-LUE (aucun `readAt`). Renfort du libellé « Non lu » dans l'UI. */
export function isUnread(item: Pick<NotificationItemDto, 'readAt'>): boolean {
  return item.readAt === null;
}

/** Formate un horodatage ISO 8601 en date+heure FR lisible, ou chaîne vide si invalide. */
export function formatNotificationTime(iso: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) {
    return '';
  }
  return date.toLocaleString('fr-FR', {
    day: '2-digit',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
  });
}
